import { createApiClient, statusOf } from '@bixo/ui'
import { queryOptions } from '@tanstack/react-query'
import type {
  CatalogueItem,
  CollectionPreview,
  EstimatePayload,
  HomeData,
  OpenItems,
  OrderDetail,
  OrderSummary,
  Paged,
  PartnerEstimate,
  PartnerMe,
  PaymentSubmission,
  PortalCustomer,
  PortalInvoice,
  PortalNotification,
  PortalSite,
  PortalStatement,
  ShareResult,
  TeamMember,
} from './types'

/*
 * The partner portal talks to /api/v1/partner with its own session guard. It
 * has its own client so a 401 here sends the user to /partner/login, never to
 * the staff sign-in.
 */

let unauthorizedHandler: (() => void) | null = null

export function onPartnerUnauthorized(handler: () => void): void {
  unauthorizedHandler = handler
}

export const partnerApi = createApiClient({
  baseURL: '/api/v1/partner',
  onUnauthorized: () => unauthorizedHandler?.(),
})

const data = <T>(p: Promise<{ data: { data: T } }>): Promise<T> => p.then((r) => r.data.data)
const page = <T>(p: Promise<{ data: Paged<T> }>): Promise<Paged<T>> => p.then((r) => r.data)
type Params = Record<string, string | number | boolean | null | undefined>
const clean = (params: Params): Params =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))

export type PartnerLoginResult = { two_factor: false; user: PartnerMe } | { two_factor: true }

export const portal = {
  login: (body: { email: string; password: string; remember?: boolean }) =>
    data<PartnerLoginResult>(partnerApi.post('/auth/login', body)),
  twoFactor: (body: { code?: string; recovery_code?: string }) =>
    data<{ user: PartnerMe }>(partnerApi.post('/auth/two-factor-challenge', body)),
  logout: () => partnerApi.post('/auth/logout'),
  invitation: (token: string) =>
    data<{ name: string; email: string; partner_name: string; business_name: string; expires_at: string }>(
      partnerApi.get(`/invitations/${encodeURIComponent(token)}`),
    ),
  accept: (token: string, body: { password: string; password_confirmation: string }) =>
    data<{ user: PartnerMe }>(partnerApi.post(`/invitations/${encodeURIComponent(token)}/accept`, body)),

  me: () => data<PartnerMe>(partnerApi.get('/me')),
  updateMe: (body: { name: string; phone?: string | null }) => data<PartnerMe>(partnerApi.patch('/me', body)),
  password: (body: { current_password: string; password: string; password_confirmation: string }) =>
    partnerApi.put('/me/password', body),
  twoFactorSetup: () =>
    data<{ secret: string; secret_formatted: string; otpauth_url: string }>(
      partnerApi.post('/me/two-factor'),
    ),
  twoFactorConfirm: (code: string) =>
    data<{ recovery_codes: string[] }>(partnerApi.post('/me/two-factor/confirm', { code })),
  twoFactorDisable: (password: string) => partnerApi.delete('/me/two-factor', { data: { password } }),
  recoveryCodes: (password: string) =>
    data<{ recovery_codes: string[] }>(partnerApi.post('/me/two-factor/recovery-codes', { password })),

  home: () => data<HomeData>(partnerApi.get('/home')),
  notifications: () => data<PortalNotification[]>(partnerApi.get('/notifications')),
  readNotifications: (ids?: number[]) => partnerApi.post('/notifications/read', ids ? { ids } : {}),
  uploadLogo: (file: File) => {
    const body = new FormData()
    body.append('logo', file)
    return data<{ logo_url: string }>(partnerApi.post('/profile/logo', body))
  },

  team: () => data<TeamMember[]>(partnerApi.get('/team')),
  inviteMember: (body: { name: string; email: string; phone?: string | null; permissions: string[] }) =>
    data<{ user: TeamMember }>(partnerApi.post('/team', body)),
  updateMember: (id: number, body: { permissions?: string[]; is_active?: boolean }) =>
    data<TeamMember>(partnerApi.patch(`/team/${id}`, body)),

  catalogue: (params: Params) => page<CatalogueItem>(partnerApi.get('/catalogue', { params: clean(params) })),
  categories: () =>
    data<{ id: number; name: string; parent_id: number | null }[]>(partnerApi.get('/catalogue/categories')),

  customers: (params: Params) =>
    page<PortalCustomer>(partnerApi.get('/customers', { params: clean(params) })),
  customer: (id: number) => data<PortalCustomer>(partnerApi.get(`/customers/${id}`)),
  saveCustomer: (id: number | null, body: Record<string, unknown>) =>
    data<PortalCustomer>(
      id ? partnerApi.patch(`/customers/${id}`, body) : partnerApi.post('/customers', body),
    ),
  addSite: (customerId: number, body: Record<string, unknown>) =>
    data<PortalSite>(partnerApi.post(`/customers/${customerId}/sites`, body)),
  updateSite: (id: number, body: Record<string, unknown>) =>
    data<PortalSite>(partnerApi.patch(`/sites/${id}`, body)),

  estimates: (params: Params) =>
    page<PartnerEstimate>(partnerApi.get('/estimates', { params: clean(params) })),
  estimate: (id: number) => data<PartnerEstimate>(partnerApi.get(`/estimates/${id}`)),
  preview: (body: EstimatePayload) => data<PartnerEstimate>(partnerApi.post('/estimates/preview', body)),
  saveEstimate: (id: number | null, body: EstimatePayload) =>
    data<PartnerEstimate>(
      id ? partnerApi.patch(`/estimates/${id}`, body) : partnerApi.post('/estimates', body),
    ),
  estimateStatus: (id: number, status: 'sent' | 'accepted' | 'rejected') =>
    data<PartnerEstimate>(partnerApi.post(`/estimates/${id}/status`, { status })),
  placeOrder: (id: number) => data<{ order: OrderSummary }>(partnerApi.post(`/estimates/${id}/order`)),
  shareEstimate: (id: number, phone?: string | null) =>
    data<ShareResult>(partnerApi.post(`/estimates/${id}/share`, phone ? { phone } : {})),
  estimateUrl: (id: number, format: 'pdf' | 'png' | 'html') => `/api/v1/partner/estimates/${id}/${format}`,

  orders: (params: Params) => page<OrderSummary>(partnerApi.get('/orders', { params: clean(params) })),
  order: (id: number) => data<OrderDetail>(partnerApi.get(`/orders/${id}`)),
  invoices: (params: Params) => page<PortalInvoice>(partnerApi.get('/invoices', { params: clean(params) })),
  invoice: (id: number) => data<PortalInvoice>(partnerApi.get(`/invoices/${id}`)),
  invoicePdfUrl: (id: number) => `/api/v1/partner/invoices/${id}/pdf`,

  openItems: () => data<OpenItems>(partnerApi.get('/payments/open-items')),
  previewCollections: (lines: { invoice_id: number; gross: string }[]) =>
    data<CollectionPreview>(partnerApi.post('/payments/preview', { lines })),
  payments: (params: Params) =>
    page<PaymentSubmission>(partnerApi.get('/payments', { params: clean(params) })),
  submitPayment: (body: FormData) => data<PaymentSubmission>(partnerApi.post('/payments', body)),

  statement: (from?: string, to?: string) =>
    data<PortalStatement>(partnerApi.get('/statement', { params: clean({ from, to }) })),
  statementPdfUrl: (from?: string, to?: string) =>
    `/api/v1/partner/statement/pdf?${new URLSearchParams(clean({ from, to }) as Record<string, string>).toString()}`,
  consignment: () =>
    data<{ item_id: number; item_name: string; item_code: string | null; quantity: string }[]>(
      partnerApi.get('/consignment'),
    ),
}

export const ppk = {
  me: ['partner', 'me'] as const,
  home: ['partner', 'home'] as const,
  notifications: ['partner', 'notifications'] as const,
  catalogue: (params: Params) => ['partner', 'catalogue', params] as const,
  categories: ['partner', 'categories'] as const,
  customers: (params: Params) => ['partner', 'customers', params] as const,
  customer: (id: number) => ['partner', 'customer', id] as const,
  estimates: (params: Params) => ['partner', 'estimates', params] as const,
  estimate: (id: number) => ['partner', 'estimate', id] as const,
  orders: (params: Params) => ['partner', 'orders', params] as const,
  order: (id: number) => ['partner', 'order', id] as const,
  invoices: (params: Params) => ['partner', 'invoices', params] as const,
  openItems: ['partner', 'open-items'] as const,
  payments: (params: Params) => ['partner', 'payments', params] as const,
  statement: (from?: string, to?: string) => ['partner', 'statement', from, to] as const,
  consignment: ['partner', 'consignment'] as const,
  team: ['partner', 'team'] as const,
}

/** The signed-in partner user, or null when signed out (never throws for 401). */
export const partnerMeQuery = queryOptions({
  queryKey: ppk.me,
  queryFn: async (): Promise<PartnerMe | null> => {
    try {
      return await portal.me()
    } catch (error) {
      if (statusOf(error) === 401 || statusOf(error) === 419) return null
      throw error
    }
  },
  staleTime: 60_000,
  retry: false,
})

export const partnerCan = (me: PartnerMe | null | undefined, permission: string): boolean =>
  !!me && (permission === 'owner' ? me.role === 'owner' : me.permissions.includes(permission as never))
